/*!
 * JINS Today Collection (Seasonal) — SS26
 * jins-today-collection.js
 * 
 * Dependencies (theme assets):
 *   - GSAP 3.14.2 + CSSPlugin + ScrollTrigger + Observer
 *   - Lenis 1.3+
 * 
 * Prefix: jt-col
 */

document.addEventListener('DOMContentLoaded', function () {
  var root = document.querySelector('.jinsToday2026ss');
  if (!root) return;

  /* ========================================
   * Device Detection
   * ======================================== */
  var BREAKPOINT = 1023;
  var device = window.innerWidth > BREAKPOINT ? 'PC' : 'SP';

  function onDeviceChange(callback) {
    var mq = window.matchMedia('(min-width: ' + (BREAKPOINT + 1) + 'px)');
    function handler(e) {
      var newDevice = e.matches ? 'PC' : 'SP';
      if (newDevice !== device) {
        device = newDevice;
        callback(device);
      }
    }
    mq.addEventListener('change', handler);
  }

  /* ========================================
   * Scrollbar Width CSS Var
   * ======================================== */
  function setScrollbarWidth() {
    var w = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--scrollbar-width', w + 'px');
  }
  setScrollbarWidth();

  /* ========================================
   * Lenis Smooth Scroll
   * ======================================== */
  var lenis;
  var lenisOpts = device === 'SP' ? {} : {
    duration: 1.2,
    easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); }
  };

  if (typeof Lenis !== 'undefined') {
    lenis = new Lenis(lenisOpts);
    lenis.on('scroll', function () {
      if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.update();
    });
    gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
    gsap.ticker.lagSmoothing(0);
  }

  /* ========================================
   * Intersection Observer — Reveal Animations
   * ======================================== */
  function initObserveOnce() {
    var els = root.querySelectorAll('[data-js="observeOnce"]');
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.dataset.state = 'active';
          if (entry.target.classList.contains('c-btn')) {
            entry.target.addEventListener('animationend', function handler() {
              entry.target.dataset.state = '';
              entry.target.removeEventListener('animationend', handler);
            });
          }
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px', threshold: 0 });
    els.forEach(function (el) { observer.observe(el); });
  }
  initObserveOnce();

  /* ========================================
   * Active Index Controller (Color Switcher)
   * ======================================== */
  function initActiveIndexControllers() {
    root.querySelectorAll('[data-js="activeIndexController"]').forEach(function (controller) {
      var items = controller.querySelectorAll('[data-index]');
      var triggers = controller.querySelectorAll('[data-js="activeIndexControllerChange"]');

      function update() {
        var active = controller.dataset.activeIndex;
        items.forEach(function (item) {
          item.dataset.active = String(item.dataset.index === active);
        });
      }

      triggers.forEach(function (trigger) {
        trigger.addEventListener('click', function () {
          controller.dataset.activeIndex = trigger.dataset.index;
          update();
        });
      });

      update();
    });
  }
  initActiveIndexControllers();

  /* ========================================
   * Vanilla Carousel (jt-col-slider)
   * ======================================== */
  var SESSION_KEY = 'jt-col-slider-state-v1';
  var sliderInstances = [];

  function getSliderState() {
    try {
      var s = sessionStorage.getItem(SESSION_KEY);
      return s ? JSON.parse(s) : { SP: {}, PC: {} };
    } catch (e) { return { SP: {}, PC: {} }; }
  }

  function saveSliderState(id, index) {
    var state = getSliderState();
    if (!state[device]) state[device] = {};
    state[device][id] = index;
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  }

  function initSlider(el) {
    var id = el.dataset.id;
    var track = el.querySelector('.jt-col-slider__track');
    var slides = el.querySelectorAll('.jt-col-slider__slide');
    var prevBtn = el.querySelector('[data-js="carouselArrow"][data-direction="prev"]');
    var nextBtn = el.querySelector('[data-js="carouselArrow"][data-direction="next"]');
    var dotContainer = el.querySelector('.c-carouselDot');
    var slideCount = slides.length;

    if (!track || slideCount === 0) return;


    // Get saved position
    var state = getSliderState();
    var current = (id && state[device] && state[device][id]) || 0;
    if (current >= slideCount) current = 0;

    var isMobile = device === 'SP';
    var dots = [];

    // Build dots — desktop shows page count, mobile shows slide count
    function buildDots() {
      if (!dotContainer) return;
      dotContainer.innerHTML = '';
      dots = [];
      var isDesktopNow = window.innerWidth > BREAKPOINT;
      var dotCount = isDesktopNow ? Math.max(0, slideCount - 3) + 1 : slideCount;
      if (dotCount <= 1) return;
      for (var i = 0; i < dotCount; i++) {
        var dot = document.createElement('span');
        dot.className = 'c-carouselDot__bullet';
        dot.dataset.index = i;
        dot.addEventListener('click', function () {
          goTo(parseInt(this.dataset.index));
        });
        dotContainer.appendChild(dot);
        dots.push(dot);
      }
    }

    // Update dots
    function updateDots() {
      dots.forEach(function (dot, i) {
        dot.classList.toggle('c-carouselDot__bullet_active', i === current);
      });
    }

    // Update arrows
    var slidesPerView = isMobile ? 1 : 3;
    var arrowContainer = el.querySelector('.c-carouselArrow');

    function updateArrows() {
      // Hide arrows and dots when all slides fit on screen (desktop only)
      var isDesktopNow = window.innerWidth > BREAKPOINT;
      if (isDesktopNow && slideCount <= slidesPerView) {
        if (arrowContainer) arrowContainer.style.display = 'none';
        if (dotContainer) dotContainer.style.display = 'none';
        return;
      }
      if (arrowContainer) arrowContainer.style.display = '';
      if (dotContainer) dotContainer.style.display = '';
      var maxNav = isDesktopNow ? Math.max(0, slideCount - 3) : slideCount - 1;
      if (prevBtn) prevBtn.dataset.disabled = String(current <= 0);
      if (nextBtn) nextBtn.dataset.disabled = String(current >= maxNav);
    }

    // Go to slide
    function goTo(index) {
      if (isMobile) {
        // Mobile: step through individual slides
        current = Math.max(0, Math.min(index, slideCount - 1));
        var slide = slides[current];
        if (slide) {
          track.scrollTo({
            left: slide.offsetLeft - (track.offsetWidth - slide.offsetWidth) / 2,
            behavior: 'smooth'
          });
        }
      } else {
        // Desktop: cap at last meaningful position (no dead clicks)
        var desktopPerView = 3;
        var maxPosition = Math.max(0, slideCount - desktopPerView);
        current = Math.max(0, Math.min(index, maxPosition));
        // Use pixel offset — percentage is relative to track, not slider
        var slidePixelWidth = slides[0].offsetWidth;
        var offset = current * slidePixelWidth;
        track.style.transform = 'translateX(-' + offset + 'px)';
      }

      updateDots();
      updateArrows();

      if (id) saveSliderState(id, current);
    }

    // Arrow events
    if (prevBtn) {
      prevBtn.addEventListener('click', function () { goTo(current - 1); });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () { goTo(current + 1); });
    }

    // Mobile: track scroll position to update dots
    if (isMobile) {
      var scrollTimer;
      track.addEventListener('scroll', function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () {
          // Find closest slide to center
          var trackCenter = track.scrollLeft + track.offsetWidth / 2;
          var closest = 0;
          var closestDist = Infinity;
          slides.forEach(function (slide, i) {
            var slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
            var dist = Math.abs(slideCenter - trackCenter);
            if (dist < closestDist) {
              closestDist = dist;
              closest = i;
            }
          });
          if (closest !== current) {
            current = closest;
            updateDots();
            if (id) saveSliderState(id, current);
          }
        }, 50);
      });
    }

    // Init
    buildDots();
    goTo(current);

    sliderInstances.push({ el: el, goTo: goTo });
  }

  function initAllSliders() {
    root.querySelectorAll('.jt-col-slider').forEach(function (el) {
      var observer = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) {
          initSlider(el);
          observer.disconnect();
        }
      }, { rootMargin: '200px 0px', threshold: 0 });
      observer.observe(el);
    });
  }

  /* ========================================
   * Nav Dialog (Menu)
   * ======================================== */
  function initNav() {
    var dialog = document.querySelector('dialog[data-modal="nav"]');
    var openBtns = document.querySelectorAll('button[data-js="openDialog"][data-modal="nav"]');
    var closeBtns = document.querySelectorAll('button[data-js="closeDialog"][data-modal="nav"]');

    if (!dialog) return;

    function openNav() {
      if (dialog instanceof HTMLDialogElement) {
        if (lenis) lenis.stop();
        dialog.showModal();
        dialog.dataset.open = 'true';
      }
    }

    function closeNav() {
      if (dialog instanceof HTMLDialogElement && dialog.dataset.open === 'true') {
        dialog.addEventListener('transitionend', function handler() {
          dialog.close();
          if (lenis) lenis.start();
          dialog.removeEventListener('transitionend', handler);
        }, { once: true });
        dialog.dataset.open = 'false';
      }
    }

    openBtns.forEach(function (btn) { btn.addEventListener('click', openNav); });
    closeBtns.forEach(function (btn) { btn.addEventListener('click', closeNav); });

    dialog.addEventListener('click', function (e) {
      var rect = dialog.getBoundingClientRect();
      if (e.clientY < rect.top || e.clientY > rect.bottom ||
          e.clientX < rect.left || e.clientX > rect.right) {
        closeNav();
      }
    });
  }

  /* ========================================
   * KV Crossfade (Hero Image Switcher)
   * ======================================== */
  function initKVCrossfade() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
    gsap.registerPlugin(ScrollTrigger);

    var wrapper = root.querySelector('.jinsToday2026ssSeasonalKV_wrapper');
    var switchEl = root.querySelector('.js-switchKV');
    var img01 = switchEl ? switchEl.querySelector('.jinsToday2026ssSeasonalKV__image_01') : null;
    var img02 = switchEl ? switchEl.querySelector('.jinsToday2026ssSeasonalKV__image_02') : null;

    if (!switchEl || !img01 || !img02 || !wrapper) return;

    var isSP = device === 'SP';
    var breadcrumbs = root.querySelector('.jinsToday2026ssBreadcrumbs');
    var headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    var breadcrumbHeight = breadcrumbs ? breadcrumbs.getBoundingClientRect().height : 0;
    var triggerEl = isSP ? wrapper : switchEl;
    var marginTop = parseFloat(window.getComputedStyle(triggerEl).marginTop) || 0;
    var startOffset = isSP
      ? headerHeight + breadcrumbHeight + parseFloat(window.getComputedStyle(wrapper).marginTop)
      : headerHeight + breadcrumbHeight + marginTop;

    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: triggerEl,
        start: 'top top+=' + startOffset + 'px',
        end: '+=150%',
        scrub: 1,
        pin: true,
        pinSpacing: true,
        invalidateOnRefresh: true,
        refreshPriority: 3
      }
    });

    tl.to(img01, { opacity: 0, duration: isSP ? 0.4 : 0.8, ease: 'none' }, 0);
    tl.to(img02, { opacity: 1, duration: isSP ? 0.4 : 0.8, ease: 'none' }, 0);
    tl.to({}, { duration: isSP ? 0.1 : 0.5 });

    ScrollTrigger.refresh();
  }

  /* ========================================
   * Fixed Sidebar Images (Desktop Only)
   * ======================================== */
  function initFixedImages() {
    if (device !== 'PC') return;
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

    var blocks = root.querySelectorAll('.jinsToday2026ssSeasonalBlock__body');
    blocks.forEach(function (block, i) {
      var fixedImg = block.querySelector('.js-fixedImage');
      if (!fixedImg) return;

      var viewportH = window.innerHeight;
      var headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      var imgH = fixedImg.clientHeight;
      var imgTop = parseFloat(window.getComputedStyle(fixedImg).top) || 0;
      var offset = (viewportH - headerH - imgH) / 2 - imgTop;

      ScrollTrigger.create({
        trigger: block,
        start: 'top ' + (headerH + offset) + 'px',
        end: 'bottom bottom',
        pin: fixedImg,
        pinSpacing: false,
        refreshPriority: blocks.length - i
      });
    });

    ScrollTrigger.sort();
    ScrollTrigger.refresh();
  }

  /* ========================================
   * ScrollTrigger Scrub Sections
   * ======================================== */
  function initScrollScrub() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

    var sections = root.querySelectorAll('[data-js="scrollTriggerScrubScroll"]');
    sections.forEach(function (section, index) {
      var trigger = section.querySelector('[data-js="scrollTriggerScrubScrollTrigger"]');
      var target = section.querySelector('[data-js="scrollTriggerScrubScrollTarget"]');
      var heightEl = section.querySelector('[data-js="scrollTriggerScrubScrollHeight"]');
      if (!trigger || !target || !heightEl) return;

      var diff = target.offsetHeight - heightEl.clientHeight;

      ScrollTrigger.create({
        id: 'id' + index,
        trigger: trigger,
        start: '+=0 70px',
        end: '+=' + diff,
        scrub: true,
        pin: true,
        invalidateOnRefresh: true,
        onToggle: function (self) {
          trigger.dataset.pin = String(self.isActive);
        },
        onUpdate: function (self) {
          var offset = self.progress * diff;
          section.style.marginBottom = '-' + (diff - offset) + 'px';
          target.style.translate = '0 -' + offset + 'px';
        }
      });
    });
  }

  /* ========================================
   * Init Sequence
   * ======================================== */
  initNav();
  initAllSliders();
  initKVCrossfade();

  if (device === 'PC') {
    window.addEventListener('load', function () {
      setTimeout(function () {
        initScrollScrub();
        initFixedImages();
      }, 300);
    });
  }

  onDeviceChange(function (newDevice) {
    if (newDevice === 'PC') {
      setTimeout(function () {
        initScrollScrub();
        initFixedImages();
      }, 300);
    }
  });
});
